import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import './propertyDetails.css';
import './propertiesUtilities.css';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const COST_FIELDS = [
    { key: 'waterCost', label: 'Water' },
    { key: 'gasCost', label: 'Gas' },
    { key: 'energyCost', label: 'Energy' }
];

const formatCurrency = (value) => `$${(Number(value) || 0).toFixed(2)}`;
const toInputValue = (value) => (Number(value) ? String(value) : '');

const PropertiesUtilitiesPage = () => {
    const navigate = useNavigate();
    const today = new Date();
    const [month, setMonth] = useState(today.getMonth() + 1);
    const [year, setYear] = useState(today.getFullYear());
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [message, setMessage] = useState({ type: '', text: '' });

    const yearOptions = useMemo(() => {
        const first = Math.min(year, today.getFullYear() - 6);
        const last = Math.max(year, today.getFullYear() + 1);
        return Array.from({ length: last - first + 1 }, (_, index) => first + index);
    }, [year]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadMonth = useCallback(async () => {
        setLoading(true);
        setLoadError('');
        try {
            const response = await api.get(`/utilityCharge/month?year=${year}&month=${month}`);
            const properties = Array.isArray(response?.data?.properties) ? response.data.properties : [];
            setRows(properties.map((property) => ({
                propertyId: property.propertyId,
                name: property.name,
                address: property.address,
                canWrite: property.canWrite,
                waterCost: toInputValue(property.waterCost),
                gasCost: toInputValue(property.gasCost),
                energyCost: toInputValue(property.energyCost)
            })));
            setDirty(false);
        } catch (error) {
            console.error('failed to load monthly utility charges', error);
            setRows([]);
            setLoadError('Unable to load utility charges for this month.');
        } finally {
            setLoading(false);
        }
    }, [month, year]);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, []);

    useEffect(() => {
        loadMonth();
    }, [loadMonth]);

    const changePeriod = (nextMonth, nextYear) => {
        if (dirty && !window.confirm('You have unsaved utility amounts. Discard them and switch months?')) return;
        setMessage({ type: '', text: '' });
        setMonth(nextMonth);
        setYear(nextYear);
    };

    const shiftMonth = (delta) => {
        const index = year * 12 + (month - 1) + delta;
        changePeriod((index % 12) + 1, Math.floor(index / 12));
    };

    const onCostChange = (propertyId, field, value) => {
        setDirty(true);
        setRows((current) => current.map((row) => (row.propertyId === propertyId ? { ...row, [field]: value } : row)));
    };

    const onSave = async (event) => {
        event.preventDefault();
        const writableRows = rows.filter((row) => row.canWrite);
        if (writableRows.length === 0) {
            setMessage({ type: 'error', text: 'You do not have permission to edit utility charges for these properties.' });
            return;
        }

        setSaving(true);
        setMessage({ type: '', text: '' });
        try {
            await api.post('/utilityCharge/month', {
                year,
                month,
                properties: writableRows.map((row) => ({
                    propertyId: row.propertyId,
                    waterCost: parseFloat(row.waterCost) || 0,
                    gasCost: parseFloat(row.gasCost) || 0,
                    energyCost: parseFloat(row.energyCost) || 0
                }))
            });
            setDirty(false);
            setMessage({ type: 'success', text: `${MONTH_NAMES[month - 1]} ${year} utility charges saved successfully.` });
        } catch (error) {
            console.error('failed to save monthly utility charges', error);
            setMessage({ type: 'error', text: error?.response?.data?.error || 'Failed to save utility charges; please try again.' });
        } finally {
            setSaving(false);
        }
    };

    const totals = COST_FIELDS.reduce((map, { key }) => ({
        ...map,
        [key]: rows.reduce((sum, row) => sum + (Number(row[key]) || 0), 0)
    }), {});
    const totalUtilities = COST_FIELDS.reduce((sum, { key }) => sum + totals[key], 0);

    return (
        <div className="property-pnl-page property-utilities-page">
            <div className="property-pnl-toolbar">
                <button className="btn-secondary" onClick={() => navigate('/Properties')}>
                    ← Back to Properties
                </button>
                <div className="property-pnl-title-block">
                    <h2>Monthly Utility Charges</h2>
                    <p>Enter water, gas, and energy amounts for every property in {MONTH_NAMES[month - 1]} {year}.</p>
                </div>
                <div className="pnl-controls utilities-period-controls">
                    <button type="button" className="btn-secondary" onClick={() => shiftMonth(-1)} aria-label="Previous month">‹</button>
                    <label htmlFor="utilitiesMonthPage">Month</label>
                    <select id="utilitiesMonthPage" value={month} onChange={(event) => changePeriod(Number(event.target.value), year)}>
                        {MONTH_NAMES.map((name, index) => <option key={name} value={index + 1}>{name}</option>)}
                    </select>
                    <label htmlFor="utilitiesYearPage">Year</label>
                    <select id="utilitiesYearPage" value={year} onChange={(event) => changePeriod(month, Number(event.target.value))}>
                        {yearOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                    <button type="button" className="btn-secondary" onClick={() => shiftMonth(1)} aria-label="Next month">›</button>
                </div>
            </div>

            {loading && (
                <div className="app-state-card app-state-info" role="status" aria-live="polite">
                    <p>Loading utility charges...</p>
                </div>
            )}

            {!loading && loadError && (
                <div className="app-state-card app-state-error" role="alert">
                    <p>{loadError}</p>
                    <div>
                        <button type="button" className="btn-primary" onClick={loadMonth}>Retry</button>
                    </div>
                </div>
            )}

            {message.text && (
                <div className={`app-notice ${message.type === 'success' ? 'app-notice-success' : 'app-notice-error'}`}>
                    {message.text}
                </div>
            )}

            <div className="investment-summary-grid utility-summary-grid">
                {COST_FIELDS.map(({ key, label }) => (
                    <div className="property-pnl-card investment-summary-card" key={key}>
                        <span className="investment-summary-label">{label} Total</span>
                        <strong>{formatCurrency(totals[key])}</strong>
                    </div>
                ))}
                <div className="property-pnl-card investment-summary-card">
                    <span className="investment-summary-label">Utility Total</span>
                    <strong>{formatCurrency(totalUtilities)}</strong>
                </div>
            </div>

            <div className="property-pnl-card">
                <form onSubmit={onSave} className="utility-page-form">
                    {loading || loadError ? (
                        <></>
                    ) : rows.length === 0 ? (
                        <div className="app-state-card app-state-info">
                            <p>No properties with utility access were found.</p>
                            <div>
                                <button type="button" className="btn-primary" onClick={() => navigate('/Properties/new')}>Create a Property</button>
                            </div>
                        </div>
                    ) : (
                        <div className="property-pnl-table-wrap">
                            <table className="utilities-table property-utility-table">
                                <thead>
                                    <tr>
                                        <th>Property</th>
                                        {COST_FIELDS.map(({ key, label }) => <th key={key}>{label}</th>)}
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => {
                                        const rowTotal = COST_FIELDS.reduce((sum, { key }) => sum + (Number(row[key]) || 0), 0);
                                        return (
                                            <tr key={row.propertyId}>
                                                <td>
                                                    <span className="utility-property-name">{row.name}</span>
                                                    {row.address && <span className="utility-property-address">{row.address}</span>}
                                                    {!row.canWrite && <span className="utility-property-address">View only</span>}
                                                </td>
                                                {COST_FIELDS.map(({ key, label }) => (
                                                    <td key={key}>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={row[key]}
                                                            disabled={!row.canWrite}
                                                            aria-label={`${label} for ${row.name}`}
                                                            onChange={(event) => onCostChange(row.propertyId, key, event.target.value)}
                                                            placeholder="0.00"
                                                        />
                                                    </td>
                                                ))}
                                                <td>{formatCurrency(rowTotal)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="modal-buttons utility-page-actions">
                        <button type="submit" className="btn-primary" disabled={loading || saving || rows.length === 0}>
                            {saving ? 'Saving...' : 'Save Utility Charges'}
                        </button>
                        <button type="button" className="btn-secondary" onClick={() => navigate('/Properties')}>Back to Properties</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PropertiesUtilitiesPage;
